import type { AgentAdoptionTierKey } from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight, primaryBase, primaryDarkest, primaryLighter, skyBase } from '../../../styles/design-tokens';

/** Main-section view, selected in the sidebar filter panel. */
type AdoptionView = 'trends' | 'teams' | 'attribution';

/** Which series the Trends chart plots. */
type AdoptionTrendMeasure = 'active_users' | 'sessions' | 'adoption_rate';

/**
 * One attribution tier's static config: which tier, labels, and color. The
 * label/info keys are template-literal typed so the full keys stay inside
 * i18next's statically-typed key union (the MetricNameKey trick).
 */
interface AdoptionTierConfig {
  key: AgentAdoptionTierKey;
  /** Full i18n key of the tier label. */
  labelKey: `agent_miner.adoption.tiers.${AgentAdoptionTierKey}`;
  /** Full i18n key of the tier's explanation tooltip. */
  infoKey: `agent_miner.adoption.tier_info.${AgentAdoptionTierKey}`;
  /** Design-token color; a sequential trust scale (darkest = strongest signal). */
  color: string;
  /** Icon shown on the tier's summary card. */
  icon: string;
}

/**
 * The five attribution tiers in resolution order (strongest signal first,
 * unattributed last). Work-based tiers wear the green trust scale; the
 * membership fallback is the palest step and unattributed is deliberately
 * gray — it is a held-out residue, not a team assignment.
 */
const ADOPTION_TIERS: AdoptionTierConfig[] = [
  {
    key: 'task_linked',
    labelKey: 'agent_miner.adoption.tiers.task_linked',
    infoKey: 'agent_miner.adoption.tier_info.task_linked',
    color: primaryDarkest,
    icon: 'task_alt',
  },
  {
    key: 'branch_inferred',
    labelKey: 'agent_miner.adoption.tiers.branch_inferred',
    infoKey: 'agent_miner.adoption.tier_info.branch_inferred',
    color: primaryBase,
    icon: 'fork_right',
  },
  {
    key: 'directory_inferred',
    labelKey: 'agent_miner.adoption.tiers.directory_inferred',
    infoKey: 'agent_miner.adoption.tier_info.directory_inferred',
    color: primaryLighter,
    icon: 'folder',
  },
  {
    key: 'member_inferred',
    labelKey: 'agent_miner.adoption.tiers.member_inferred',
    infoKey: 'agent_miner.adoption.tier_info.member_inferred',
    color: inkLight,
    icon: 'group',
  },
  {
    key: 'unattributed',
    labelKey: 'agent_miner.adoption.tiers.unattributed',
    infoKey: 'agent_miner.adoption.tier_info.unattributed',
    color: skyBase,
    icon: 'help_center',
  },
];

export { ADOPTION_TIERS };
export type { AdoptionTierConfig, AdoptionTrendMeasure, AdoptionView };
